import {
  ComponentPropsWithoutRef,
  ComponentType,
  FormEvent,
  ReactNode,
  useState,
} from 'react';
import styled from 'styled-components';

import YandexInvisibleCaptcha from './YandexInvisibleCaptcha';
import { useInvisibleCaptchaSubmit } from './hooks';
import CaptchaErrorModal from './CaptchaErrorModal';

const captchaErrorMessage =
  'Не удается проверить капчу, обратитесь к администратору';

type CaptchaFormChildren =
  | ReactNode
  | ((params: { isCaptchaSubmitting: boolean }) => ReactNode);

export type CaptchaSubmitValues<T extends object> = T & {
  captchaToken: string;
};

export type CaptchaFormBindingProps<TValues extends object> = Pick<
  CaptchaFormProps<TValues>,
  'onCaptchaSubmit' | 'validateForm' | 'getValues'
>;

export interface CaptchaFormProps<TValues extends object = Record<string, unknown>>
  extends Omit<ComponentPropsWithoutRef<'form'>, 'children' | 'onSubmit'> {
  children: CaptchaFormChildren;
  onCaptchaSubmit?: (values: CaptchaSubmitValues<TValues>) => unknown;
  onSubmitWithoutCaptcha?: (values: TValues) => unknown;
  shouldSubmitWithCaptcha?: (values: TValues) => boolean;
  validateForm: () => Promise<boolean>;
  getValues: () => TValues;
}

const CaptchaForm = <TValues extends object = Record<string, unknown>>({
  children,
  onCaptchaSubmit,
  onSubmitWithoutCaptcha,
  shouldSubmitWithCaptcha,
  validateForm,
  getValues,
  ...props
}: CaptchaFormProps<TValues>) => {
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [isCaptchaErrorModalOpen, setCaptchaErrorModalOpen] = useState(false);
  const {
    captchaKey,
    captchaProps,
    handleSubmit,
    isSubmitting: isCaptchaSubmitting,
  } = useInvisibleCaptchaSubmit<TValues>(onCaptchaSubmit, () => {
    setCaptchaError(captchaErrorMessage);
  });

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    const values = getValues();

    if (shouldSubmitWithCaptcha && !shouldSubmitWithCaptcha(values)) {
      return onSubmitWithoutCaptcha?.(values);
    }

    if (captchaError) {
      setCaptchaErrorModalOpen(true);
      return;
    }

    return handleSubmit(values);
  };

  const handleCaptchaErrorModalClose = () => {
    setCaptchaErrorModalOpen(false);
  };

  return (
    <form {...props} onSubmit={handleFormSubmit}>
      {typeof children === 'function'
        ? children({ isCaptchaSubmitting })
        : children}
      <CaptchaErrorModal
        title={captchaError ?? captchaErrorMessage}
        isOpen={isCaptchaErrorModalOpen}
        onClose={handleCaptchaErrorModalClose}
      />
      <YandexInvisibleCaptcha key={captchaKey} {...captchaProps} />
    </form>
  );
};

/** Preserves form values type when wrapping CaptchaForm with styled-components. */
export function captchaFormStyled<TValues extends object>() {
  return styled(CaptchaForm as ComponentType<CaptchaFormProps<TValues>>);
}

export default CaptchaForm;
